import React, { useState } from 'react';
import { User, Mail, Phone, Shield, Edit3, Save, X, Crown, GraduationCap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, loadUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/auth/profile', form);
      await loadUser();
      setEditing(false);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({ name: user?.name || '', phone: user?.phone || '' });
    setEditing(false);
  };

  const infoRows = [
    { Icon: User, label: 'Full Name', value: user?.name, field: 'name', editable: true },
    { Icon: Mail, label: 'Email Address', value: user?.email, editable: false },
    { Icon: Phone, label: 'Phone Number', value: user?.phone || 'Not provided', field: 'phone', editable: true },
    { Icon: Shield, label: 'Account Role', value: user?.role === 'admin' ? 'Administrator' : 'Student', editable: false },
  ];

  return (
    <div style={{ paddingTop: 68, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        background: `radial-gradient(ellipse at 20% 50%, rgba(0,200,255,0.07) 0%, transparent 55%), var(--ac-navy)`,
        borderBottom: '1px solid var(--ac-border)', padding: '3rem 0',
      }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: 'var(--grad-edu)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, fontWeight: 800, color: 'white', flexShrink: 0,
            }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--ac-white)', marginBottom: 4 }}>
                {user?.name}
              </h1>
              <p style={{ fontSize: 14, color: 'var(--ac-muted)' }}>{user?.email}</p>
              <div className="badge badge-purple" style={{ marginTop: 8 }}>
                {user?.role === 'admin' ? <><Crown size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Administrator</> : <><GraduationCap size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Student</>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container" style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem', maxWidth: 700 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.8rem' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ac-white)' }}>Account Details</h2>
            {!editing ? (
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
                <Edit3 size={14} /> Edit Profile
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={loading}>
                  <Save size={14} /> {loading ? 'Saving...' : 'Save'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={handleCancel}>
                  <X size={14} /> Cancel
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            {infoRows.map(({ Icon, label, value, field, editable }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: '1rem',
                padding: '1rem 1.2rem', borderRadius: 'var(--radius-md)',
                background: 'rgba(255,255,255,0.02)', border: '1px solid var(--ac-border)',
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 'var(--radius-sm)',
                  background: 'rgba(0,200,255,0.08)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon size={16} color="var(--ac-cyan)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--ac-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>
                    {label}
                  </div>
                  {editing && editable ? (
                    <input
                      className="form-input"
                      value={form[field]}
                      onChange={e => setForm({ ...form, [field]: e.target.value })}
                      style={{ padding: '6px 10px', fontSize: 14 }}
                    />
                  ) : (
                    <div style={{ fontSize: 15, color: value === 'Not provided' ? 'var(--ac-muted)' : 'var(--ac-white)', fontWeight: 500 }}>
                      {value}
                    </div>
                  )}
                </div>
                {!editable && (
                  <div style={{ fontSize: 11, color: 'var(--ac-muted)', fontStyle: 'italic' }}>Read-only</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Account Actions */}
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--ac-white)', marginBottom: '1rem' }}>Account Actions</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm">Change Password</button>
            <button className="btn btn-sm" style={{ background: 'rgba(255,71,87,0.1)', color: 'var(--ac-danger)', border: '1px solid rgba(255,71,87,0.2)' }}>
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
